import React, { useEffect, useState } from 'react';
import { APIDirectoryHome } from '@cdk-prod/api-directory-component';
import {
  Toast,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
} from 'cdk-radial';
import { useDispatch, useSelector } from 'react-redux';
import {
  API_DIRECTORY_PAGE,
  API_DIRECTORY_THEME,
  API_TYPE_MAPPING,
  API_TYPE_TO_PARAMS_MAP,
  SEARCH_FILTER_URL_PARAMS,
  FILTER_QUERY_PARAMS,
  FILTER_ACTIONS,
} from '../../../constants/APIDirectoryConstants';
import {
  getAPIDetailsById,
  getApis,
  getAsyncApis,
  getConsentTerms,
  getDomains,
  setAPIDirectoryPage,
  getBundleDetailsById,
} from '../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';
import {
  addAPIToCart,
  getOrders,
  submitRestrictedAccessRequest,
} from '../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  sendAmplitudeData,
  getOrgAmplitudeProperties,
} from '../../../utils/amplitude';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../../constants/amplitudeConstants';
import { ADD_API_To_CART_FAILURE_MESSAGE } from '../../../constants/APIDirectoryConstants';
import { DealerAPIDirectoryTerms } from '../DealerAPIDirectoryTerms/DealerAPIDirectoryTerms';
import { MSA_CONSENT_STATUS, RESOURCE_TYPES } from '../../../constants/constants';
import { getSinglePlanId, mapCartForDirectory } from '../utils/apiPricingUtil';
import {
  trackApiAddedToCart,
  trackBundleAddedToCart,
  trackApiInformationRequested,
  trackApiFilterChanged,
} from '../../../utils/gtm';

const MY_APIS_URL = 'my-apis';
const API_DIR_URL = 'api-directory';

const TopBanner = () => {
  const LINE1 = 'Review, Test and Subscribe to APIs';
  const LINE2 = 'Next Generation APIs';
  return (
    <div className="api-directory-top-banner">
      <div className="text-container">
        <div className="heading">{LINE1}</div>
        <div className="subheading">{LINE2}</div>
      </div>
    </div>
  );
};

const APIDirectoryHomeContainer = ({
  handleMyCart,
  filters,
  setFilters,
}) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [requestStatus, setRequestState] = useState({});
  const [requestId, setRequestId] = useState(null);
  const {
    authenticationToken: token,
    fortellisEnterpriseEntityId,
    isDealerPurchaseDisabled,
    hasDAABAccess,
    isAuthenticated,
    entityDetails,
    decodedToken: { firstName, lastName } = {},
    userEmail,
  } = state?.loginDetailsSlice;
  const {
    cart,
    orders,
    errorAddingApiToCart,
    addingApiToCart,
    requestingRestrictedAccess,
    errorRequestingRestrictedAccess,
    restrictedAccessRequested,
    requestingApiId,
  } = state?.orderAPISlice;
  const {
    domains,
    domainsLoading,
    apis,
    restApisLoading,
    asyncApis,
    asyncApisLoading,
    componentToRender,
    updatedBundles,
    daabConsentDetails,
    fetchingDAABConsentDetails,
  } = state.apiDirectorySlice;

  const [showTerms, setShowTerms] = useState(false);
  const isMsaConsented =
    daabConsentDetails?.consentStatus === MSA_CONSENT_STATUS.CONSENTED;

  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );
  const authProp = {
    isAuthenticated,
    userData: {
      name: `${firstName || ''} ${lastName || ''}`,
      email: userEmail,
    },
    orgData: {
      id: fortellisEnterpriseEntityId,
      name: entityDetails?.organizationName || '',
    },
  };

  const mappedCart = React.useMemo(() => mapCartForDirectory(cart), [cart]);

  const handleApiRedirection = ({ apiId, apiType }) => {
    dispatch(getAPIDetailsById({ apiId, apiType, token }));
    dispatch(
      setAPIDirectoryPage({
        page: API_DIRECTORY_PAGE.DETAILS,
        props: { apiId },
      })
    );
  };

  const viewApiPricingTab = ({ apiId, apiType }) => {
    const location = {
      search: 'q=Pricing',
    };
    dispatch(getAPIDetailsById({ apiId, apiType, token }));
    dispatch(
      setAPIDirectoryPage({
        page: API_DIRECTORY_PAGE.DETAILS,
        props: { apiId, location: location },
      })
    );
  };
  const handleClearDealerFilters = ({ apiType, category, filterType }) => {
    setFilters({
      selectedAPIType: apiType,
      selectedDomainIds: category,
      selectedFilterType: filterType,
    });
  };

  const getFilteredResultsCount = (updatedFilters) => {
    const { selectedAPIType = [], selectedDomainIds = [], selectedFilterType = [] } = updatedFilters;
    let apisList = apis || [];
    let asyncApisList = asyncApis || [];
    let bundlesList = updatedBundles || [];

    if (selectedDomainIds.length > 0) {
      const lowercaseCategories = selectedDomainIds.map((cat) => cat.toLowerCase());
      const filterCategory = (list) =>
        list.filter((api) =>
          Array.isArray(api.tags) &&
          api.tags.some((t) =>
            typeof t === 'string'
              ? lowercaseCategories.includes(t.toLowerCase())
              : lowercaseCategories.includes(t.name ? t.name.toLowerCase() : '')
          )
        );
      apisList = filterCategory(apisList);
      asyncApisList = filterCategory(asyncApisList);
      bundlesList = [];
    }

    let filteredApis = [];
    let filteredBundles = [];

    if (selectedAPIType.length > 0) {
      const showRest = selectedAPIType.includes('REST');
      const showAsync = selectedAPIType.includes('Async');
      const showBundles = selectedAPIType.includes('Bundles');

      if (showRest) filteredApis = filteredApis.concat(apisList);
      if (showAsync) filteredApis = filteredApis.concat(asyncApisList);
      if (showBundles) filteredBundles = bundlesList;
    } else {
      filteredApis = [...apisList, ...asyncApisList];
      filteredBundles = bundlesList;
    }

    filteredApis = filteredApis.filter((api) => api.name && api.description);
    filteredApis = [...new Set(filteredApis.map((x) => x.id))].map((id) =>
      filteredApis.find((x) => x.id === id)
    );

    const hasBundleFilterSelected = selectedFilterType.includes('Bundles');
    const hasContractedFilterSelected = selectedFilterType.includes('Contracted');
    const hasSelectedFilters = selectedFilterType.length > 0;

    if (hasSelectedFilters) {
      if (!hasBundleFilterSelected) {
        filteredBundles = [];
      }
      if (hasContractedFilterSelected) {
        filteredApis = filteredApis.filter((api) => api.isContracted === true);
      }
      if (hasBundleFilterSelected && selectedDomainIds.length > 0) {
        filteredBundles = [];
      }
      if (!hasContractedFilterSelected) {
        filteredApis = [];
      }
    } else if (selectedDomainIds.length > 0) {
      filteredBundles = [];
    }

    return filteredApis.length + filteredBundles.length;
  };

  const handleDealerFiltersRedirection = ({ searchKey, itemKey }) => {
    if (!searchKey && !itemKey) {
      const clearedFilters = {
        selectedAPIType: [],
        selectedDomainIds: [],
        selectedFilterType: [],
        newUrl: '/',
      };
      setFilters(clearedFilters);
      return;
    }

    const queryParam = SEARCH_FILTER_URL_PARAMS[searchKey] || searchKey;
    const selectedAPIType = [...filters.selectedAPIType];
    const selectedDomainIds = [...filters.selectedDomainIds];
    const selectedFilterType = [...filters.selectedFilterType];

    if (queryParam === FILTER_QUERY_PARAMS.TYPE) {
      const readable = API_TYPE_MAPPING[itemKey] || itemKey;
      if (selectedAPIType.includes(readable)) {
        selectedAPIType.splice(selectedAPIType.indexOf(readable), 1);
      } else {
        selectedAPIType.push(readable);
      }
    } else if (queryParam === FILTER_QUERY_PARAMS.CATEGORY) {
      if (selectedDomainIds.includes(itemKey)) {
        selectedDomainIds.splice(selectedDomainIds.indexOf(itemKey), 1);
      } else {
        selectedDomainIds.push(itemKey);
      }
    } else if (queryParam === FILTER_QUERY_PARAMS.GROUP) {
      if (selectedFilterType.includes(itemKey)) {
        selectedFilterType.splice(selectedFilterType.indexOf(itemKey), 1);
      } else {
        selectedFilterType.push(itemKey);
      }
    }

    const urlParams = new URLSearchParams();

    if (selectedFilterType.length > 0)
      urlParams.set('Group', selectedFilterType.join(','));
    if (selectedAPIType.length > 0) {
      const rawTypes = selectedAPIType.map(
        (type) => API_TYPE_TO_PARAMS_MAP[type] || type
      );
      urlParams.set('Type', rawTypes.join(','));
    }
    if (selectedDomainIds.length > 0)
      urlParams.set('Category', selectedDomainIds.join(','));

    const newUrl = `/?${urlParams.toString()}`;

    const updatedFilters = {
      selectedAPIType,
      selectedDomainIds,
      selectedFilterType,
    };

    const count = getFilteredResultsCount(updatedFilters);

    // Determine if filter was added or removed
    const previousFilters = filters;
    let wasAdded = false;
    if (queryParam === FILTER_QUERY_PARAMS.TYPE) {
      wasAdded = !previousFilters.selectedAPIType.includes(
        API_TYPE_MAPPING[itemKey] || itemKey
      );
    } else if (queryParam === FILTER_QUERY_PARAMS.CATEGORY) {
      wasAdded = !previousFilters.selectedDomainIds.includes(itemKey);
    } else if (queryParam === FILTER_QUERY_PARAMS.GROUP) {
      wasAdded = !previousFilters.selectedFilterType.includes(itemKey);
    }

    trackApiFilterChanged({
      filterType: queryParam,
      filterValue: itemKey,
      action: wasAdded ? FILTER_ACTIONS.ADDED : FILTER_ACTIONS.REMOVED,
      activeFilters: {
        api_group: selectedFilterType,
        api_type: selectedAPIType,
        categories: selectedDomainIds,
      },
      resultsCount: count,
    });

    setFilters({
      newUrl,
      selectedAPIType,
      selectedDomainIds,
      selectedFilterType,
    });
  };

  const redirectToMyAPIs = () => {
    sendAmplitudeData(AMPLITUDE_EVENTS.DEALER_API_TILE_VIEW_IN_MY_APIS, orgAmplitudeProperties);
    let url = window.location.href.split(`/${API_DIR_URL}`)[0];
    localStorage.setItem('dplocationWithoutApp', `${url}/${MY_APIS_URL}`);
    window.location.assign(`${url}/${MY_APIS_URL}`);
  };

  const handleViewBundleDetails = (bundleDetails) => {
    dispatch(getBundleDetailsById({ bundleId: bundleDetails.id, token }));
    dispatch(
      setAPIDirectoryPage({
        page: API_DIRECTORY_PAGE.DETAILS,
        props: { bundleDetailsViewEnabled: true },
      }),
    );
  };

  const addApiToCart = (payload) => {
    const { resourceId, resourceType } = payload;
    setRequestId(resourceId);
    setRequestState((prev) => ({
      ...prev,
      [resourceId]: { isLoading: true, error: false },
    }));

    // Auto-add pricing plan for APIs with single plan (excludes bundles)
    const getApiData = () => {
      if (resourceType === RESOURCE_TYPES.BUNDLE) return null;
      if (resourceType === RESOURCE_TYPES.API) return apis?.find(api => api.id === resourceId);
      if (resourceType === RESOURCE_TYPES.ASYNC_API) return asyncApis?.find(api => api.id === resourceId);
      return null;
    };

    const apiData = getApiData();
    const pricingPlanId = getSinglePlanId(apiData?.plans);
    const plan = pricingPlanId ? apiData?.plans?.find(p => p.id === pricingPlanId) : null;
    trackApiAddedToCart(apiData, plan, cart.length + 1);

    const cartPayload = {
      cart: [
        {
          resourceId,
          resourceType,
          ...(pricingPlanId && { pricingPlanId }),
        },
      ],
    };

    dispatch(addAPIToCart({ payload: cartPayload, token }));
    sendAmplitudeData(AMPLITUDE_EVENTS.ADD_TO_CART, {
      [AMPLITUDE_PROPERTIES.RESOURCE_TYPE]: resourceType || '',
      [AMPLITUDE_PROPERTIES.RESOURCE_ID]: resourceId || '',
    });
  };

  const requestRestrictedAccess = (payload) => {
    const { apiId, apiType } = payload;
    setRequestId(apiId);
    setRequestState((prev) => ({
      ...prev,
      [apiId]: { isLoading: true, error: false },
    }));

    const apiData = apis?.find((api) => api.id === apiId) || asyncApis?.find((api) => api.id === apiId);
    trackApiInformationRequested(apiData, cart.length);

    dispatch(submitRestrictedAccessRequest({ apiId, apiType, token }));
  };

  const handleAddBundleIntoCart = (payload) => {
    const { bundleId } = payload;
    setRequestId(bundleId);
    setRequestState((prev) => ({
      ...prev,
      [bundleId]: { isLoading: true, error: false },
    }));
    
    // Auto-add pricing plan if bundle has only one plan
    const bundleData = updatedBundles?.find(bundle => bundle.id === bundleId);
    const singlePlanId = getSinglePlanId(bundleData?.plans);
    const plan = singlePlanId ? bundleData?.plans?.find(p => p.id === singlePlanId) : null;

    const hasPlans = bundleData?.plans && bundleData.plans.length > 0;
    const apisCount = (!hasPlans && bundleData?.apis) ? bundleData.apis.length : 1;
    trackBundleAddedToCart(bundleData, plan, cart.length + apisCount);
    
    let updatedPayload = { ...payload };
    if (!hasPlans && payload.cart && Array.isArray(payload.cart)) {
      const apiMap = new Map(bundleData?.apis?.map((api) => [api.id, api]));
      updatedPayload.cart = payload.cart.map((cartItem) => {
        const matchingApi = apiMap.get(cartItem.resourceId);
        if (matchingApi) {
          const apiSinglePlanId = getSinglePlanId(matchingApi.plans);
          if (apiSinglePlanId) {
            return {
              ...cartItem,
              pricingPlanId: apiSinglePlanId,
            };
          }
        }
        return cartItem;
      });
    } else if (singlePlanId) {
      updatedPayload = { ...payload, bundlePricingPlanId: singlePlanId };
    }
    
    dispatch(addAPIToCart({ payload: updatedPayload, token }));
  };

  useEffect(() => {
    if (requestId)
      setRequestState((prev) => ({
        ...prev,
        [requestId]: {
          isLoading: addingApiToCart,
          error: errorAddingApiToCart,
        },
      }));
  }, [addingApiToCart, errorAddingApiToCart, requestId]);

  const homePageClickHandlers = {
    addApiToCart: addApiToCart,
    handleApiRedirection: handleApiRedirection,
    viewApiPricingTab: viewApiPricingTab,
    openMyCart: handleMyCart,
    viewInMyAPIs: redirectToMyAPIs,
    viewBundleDetails: ({ bundleDetails }) =>
      handleViewBundleDetails(bundleDetails),
    addBundleIntoCart: (payload) => handleAddBundleIntoCart(payload),
    viewTermsDialog: () => setShowTerms(true),
    handleDealerFiltersRedirection: handleDealerFiltersRedirection,
    handleClearDealerFilters: handleClearDealerFilters,
    requestRestrictedAccess: requestRestrictedAccess,
  };

  useEffect(() => {
    if (
      daabConsentDetails &&
      !hasDAABAccess &&
      Object.keys(daabConsentDetails).length <= 0
    ) {
      dispatch(getConsentTerms({ token }));
    }
    if (domains && Object.keys(domains).length <= 0) {
      dispatch(getDomains({ token }));
    }
    if (apis && apis.length <= 0) {
      dispatch(getApis({ token, isDealerPurchaseDisabled }));
    }
    if (asyncApis && asyncApis.length <= 0) {
      dispatch(getAsyncApis({ token, isDealerPurchaseDisabled }));
    }
    if (orders?.restApis.length === 0 && orders?.asyncApis.length === 0) {
      dispatch(getOrders({ token, fortellisEnterpriseEntityId }));
    }
  }, []);

  useEffect(() => {
    if (requestId) {
      setRequestState((prev) => ({
        ...prev,
        [requestId]: {
          isLoading:
            addingApiToCart ||
            (requestingRestrictedAccess && requestingApiId === requestId),
          error:
            errorAddingApiToCart ||
            (errorRequestingRestrictedAccess && requestingApiId === requestId),
          success: restrictedAccessRequested && requestingApiId === requestId,
        },
      }));
    }
  }, [
    addingApiToCart,
    errorAddingApiToCart,
    requestId,
    requestingRestrictedAccess,
    errorRequestingRestrictedAccess,
    restrictedAccessRequested,
    requestingApiId,
  ]);

  return (
    <>
      {!hasDAABAccess && (
        <DealerAPIDirectoryTerms
          showTerms={showTerms}
          setShowTerms={setShowTerms}
        />
      )}
      <TopBanner />
      <APIDirectoryHome
        auth={authProp}
        isApiDirectoryView={false}
        apis={apis}
        apisLoading={restApisLoading}
        apiBundles={updatedBundles}
        asyncApis={asyncApis}
        asyncApisLoading={asyncApisLoading}
        domains={domains}
        domainsLoading={domainsLoading}
        cartDetails={mappedCart}
        orderDetails={orders}
        apiHomeOnClickHandlers={homePageClickHandlers}
        belongsTo={API_DIRECTORY_THEME.DEALER_API_DIRECTORY}
        selectedAPIType={componentToRender?.props?.selectedAPIType}
        selectedDomainIds={componentToRender?.props?.selectedDomainIds}
        sendAmplitudeData={sendAmplitudeData}
        isContractUIEnabled={true}
        requestStatus={requestStatus}
        selectedFilterType={componentToRender?.props?.selectedFilterType}
        isMsaConsented={isMsaConsented || hasDAABAccess}
        fetchingDAABConsentDetails={fetchingDAABConsentDetails}
        requestingOrg={fortellisEnterpriseEntityId}
        isDealerPurchaseDisabled={isDealerPurchaseDisabled}
      />

      {requestStatus && requestStatus[requestId]?.error && (
        <Toast
          content={ADD_API_To_CART_FAILURE_MESSAGE}
          id="error-add-api-toast"
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          position={TOAST_POSITIONS.FIXED}
          style={{ marginTop: '50px', zIndex: '10' }}
          variant={TOAST_VARIANTS.NEGATIVE}
          actions={[
            {
              onClick: () => {
                setRequestState((prev) => ({
                  ...prev,
                  [requestId]: { isLoading: false, error: false },
                }));
              },
              text: 'Dismiss',
            },
          ]}
        />
      )}
    </>
  );
};

export default APIDirectoryHomeContainer;
